import type { PropertyInputProps } from './TextInput';

interface MultiCurrencyValue {
  amount: number | string;
  unit: string;
}

/**
 * Get currency symbol from currency code using Intl API
 */
function getCurrencySymbol(currencyCode: string): string {
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);
    return formatted.replace(/[\d\s,]/g, '').trim();
  } catch {
    return '$';
  }
}

/**
 * Format a single currency value for display
 */
function formatCurrencyValue(qty: MultiCurrencyValue): string {
  const symbol = getCurrencySymbol(qty.unit);
  const numAmount =
    typeof qty.amount === 'string' ? parseFloat(qty.amount) : qty.amount;
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
  return `${symbol}${formattedAmount} ${qty.unit}`;
}

/**
 * Multi-Currency Input - Read-only display for multi-currency rollup fields.
 *
 * Used for rollup properties that aggregate values across multiple currencies
 * (e.g., Potential Deal Total, Weighted Deal Total).
 *
 * Note: Multi-currency fields are currently read-only rollups. Full editing
 * support is not yet implemented.
 */
export const MultiCurrencyInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  disabled = false,
  inputRef: _inputRef,
  placeholder: _placeholder,
}) => {
  // Parse the value - it should be an array of {amount, unit} objects
  const currencies: MultiCurrencyValue[] = Array.isArray(value)
    ? value.filter(
        v => v && typeof v === 'object' && 'amount' in v && 'unit' in v
      )
    : [];

  return (
    <div
      className={`flex flex-wrap gap-2 ${disabled ? 'opacity-50' : ''}`}
      role='group'
      aria-label={propertyType.label}
    >
      {currencies.length > 0 ? (
        currencies.map(qty => (
          <div
            key={qty.unit}
            className='px-3 py-2 bg-secondary rounded-lg border border-primary text-sm font-medium'
          >
            {formatCurrencyValue(qty)}
          </div>
        ))
      ) : (
        <div className='px-3 py-2 bg-secondary rounded-lg border border-primary text-sm text-tertiary'>
          No values
        </div>
      )}
    </div>
  );
};
